import { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Calculator, AlertTriangle, Save, Loader2, ChevronDown, ChevronRight, Plus, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { surgeryBudgetsAPI, patientAPI } from '@/lib/api';
import PatientPicker from '@/components/shared/PatientPicker';
import {
  SURGERY_STAY_CODES,
  TEST_CATEGORY_LABELS,
} from '@/lib/constants/surgeryBudgets';

/**
 * BudgetGeneratorDialog — generates a surgery cost estimate.
 *
 * Procedures are dynamic (CRUD-managed in the Configuration tab) so the
 * dropdown is populated via the API on every open. Each procedure carries
 * its surgeon_fee + anesthesia_fee directly. The doctor:
 *   • Picks procedure → surgeon + anesthesia fees auto-fill.
 *   • Enters estimated OR minutes → auto-selects the tariff tier
 *     (or flips "cirugía especial" for the QMDE flat rate).
 *   • Picks stay category + nights.
 *   • Tags optional tests via grouped checkboxes (preop / anatomía / imagen).
 *
 * Live preview re-fetches on every input change (debounced 300ms).
 */
export default function BudgetGeneratorDialog({
  open,
  onOpenChange,
  patient,
  prefillBudget,
  onPrefillConsumed,
  onSaved,
}) {
  const [procedures, setProcedures] = useState([]);
  const [procedureCode, setProcedureCode] = useState(null);
  const [orMinutes, setOrMinutes] = useState(120);
  const [orSpecial, setOrSpecial] = useState(false);
  const [stayCode, setStayCode] = useState('ES');
  const [nights, setNights] = useState(1);
  const [selectedTestCodes, setSelectedTestCodes] = useState([]);
  const [notes, setNotes] = useState('');
  const [depositPercent, setDepositPercent] = useState(50);
  const [implantRows, setImplantRows] = useState([]); // [{reference, qty, unit_price, brand?, volume_cc?, note?}]

  const [testCatalog, setTestCatalog] = useState([]);
  const [openCategories, setOpenCategories] = useState({});
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Patient binding (anonymous mode only — when a `patient` prop is passed
  // from a patient profile, the picker is hidden and that patient is locked).
  // The doctor can also leave it blank to keep the budget anonymous.
  const [pickedPatient, setPickedPatient] = useState(null);
  const effectivePatient = patient || pickedPatient;
  const effectivePatientId = effectivePatient?.id || null;

  // Inline "quick-create patient" form — toggled by the "+ Nuevo paciente"
  // button next to the picker. Minimal fields (name + phone required, DNI
  // + email optional) so the doctor never has to leave the budget flow to
  // register a walk-in lead. The created patient is automatically bound to
  // the budget via `setPickedPatient`.
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  const [newPatientDraft, setNewPatientDraft] = useState({
    name: '', phone: '', dni: '', email: '',
  });

  const resetNewPatientForm = () => {
    setCreatingPatient(false);
    setNewPatientDraft({ name: '', phone: '', dni: '', email: '' });
  };

  const submitNewPatient = async () => {
    const name = newPatientDraft.name.trim();
    const phone = newPatientDraft.phone.trim();
    if (!name || !phone) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    setSavingPatient(true);
    try {
      const payload = {
        name,
        phone,
        dni: newPatientDraft.dni.trim() || null,
        email: newPatientDraft.email.trim() || null,
      };
      const r = await patientAPI.create(payload);
      const created = r.data?.patient || r.data;
      if (created?.id) {
        setPickedPatient({
          id: created.id,
          name: created.name,
          dni: created.dni,
          nhc: created.nhc,
          phone: created.phone,
          email: created.email,
        });
        toast.success(`Paciente "${created.name}" creado`);
        resetNewPatientForm();
      } else {
        toast.error('No se ha podido crear el paciente');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear paciente');
    } finally {
      setSavingPatient(false);
    }
  };

  // Reset the picker every time the dialog opens, so re-opening for a fresh
  // anonymous budget doesn't carry the previous patient over.
  useEffect(() => {
    if (open) {
      setPickedPatient(null);
      resetNewPatientForm();
    }
  }, [open]);

  // Edit flow: when `prefillBudget` is provided, hydrate the form with its
  // saved values so the doctor can tweak and re-save (the parent then
  // deletes the original snapshot — see SurgeryBudgetsPage.startEditBudget).
  useEffect(() => {
    if (!open || !prefillBudget) return;
    const b = prefillBudget;
    setProcedureCode(b.procedure_code || null);
    const orInfo = b.breakdown?.or || {};
    setOrMinutes(orInfo.minutes ?? b.estimated_or_minutes ?? 120);
    setOrSpecial(!!(orInfo.special ?? b.or_special));
    setStayCode(b.breakdown?.stay?.code || b.stay_code || 'ES');
    setNights(b.breakdown?.stay?.nights ?? b.nights ?? 1);
    setSelectedTestCodes(
      (b.breakdown?.tests?.items || []).map(t => t.code).filter(Boolean)
      || b.selected_test_codes || []
    );
    setNotes(b.notes || '');
    const implants = (b.breakdown?.implants?.items || b.selected_implants || []).map(r => ({
      reference: r.reference || '',
      brand: r.brand || '',
      volume_cc: r.volume_cc ?? '',
      qty: r.qty || 1,
      unit_price: r.unit_price ?? '',
      note: r.note || '',
    }));
    setImplantRows(implants);
    if (b.patient_id && b.patient_name) {
      setPickedPatient({ id: b.patient_id, name: b.patient_name });
    }
    onPrefillConsumed?.();
  }, [open, prefillBudget, onPrefillConsumed]);

  const selectedProcedure = useMemo(
    () => procedures.find(p => p.code === procedureCode) || null,
    [procedureCode, procedures]
  );

  // When procedure changes, prefill implant rows from its `default_implants`
  // (when it requires implants) or clear them otherwise. The doctor can then
  // edit, add or remove rows freely before saving.
  useEffect(() => {
    if (!selectedProcedure) return;
    if (selectedProcedure.requires_implants) {
      // Laterality of the procedure drives the default qty (1 uni / 2 bi),
      // unless a default row explicitly stores qty > 1.
      const latQty = selectedProcedure.implant_laterality === 'bilateral' ? 2 : 1;
      const defaults = (selectedProcedure.default_implants || []).map(d => ({
        reference: d.reference || '',
        brand: d.brand || '',
        volume_cc: d.volume_cc ?? '',
        qty: (Number(d.qty) > 1) ? Number(d.qty) : latQty,
        unit_price: d.unit_price ?? '',
        note: d.note || '',
      }));
      setImplantRows(defaults);
    } else {
      setImplantRows([]);
    }
  }, [selectedProcedure]);

  // Load procedures + tests on dialog open
  useEffect(() => {
    if (!open) return;
    Promise.all([
      surgeryBudgetsAPI.getProcedures(),
      surgeryBudgetsAPI.getTests(),
    ]).then(([p, t]) => {
      const list = p.data.procedures || [];
      setProcedures(list);
      if (list.length > 0 && !procedureCode) {
        setProcedureCode(list[0].code);
      }
      setTestCatalog(t.data.grouped || []);
    }).catch((e) => {
      toast.error(e.response?.data?.detail || 'Error al cargar configuración del presupuesto');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced preview refresh
  const fetchPreview = useCallback(async () => {
    if (!procedureCode) return;
    setLoadingPreview(true);
    try {
      const payload = {
        patient_id: effectivePatientId,
        procedure_code: procedureCode,
        estimated_or_minutes: Number(orMinutes) || 0,
        or_special: !!orSpecial,
        stay_code: nights > 0 ? stayCode : null,
        nights: Number(nights) || 0,
        selected_test_codes: selectedTestCodes,
        selected_implants: implantRows
          .filter(r => r.reference && r.reference.trim())
          .map(r => ({
            reference: r.reference.trim(),
            brand: r.brand || null,
            volume_cc: r.volume_cc === '' ? null : Number(r.volume_cc),
            qty: Number(r.qty) || 1,
            unit_price: r.unit_price === '' || r.unit_price == null ? null : Number(r.unit_price),
            note: r.note || null,
          })),
        custom_lines: [],
        notes,
        deposit_percent: Number(depositPercent) || 50,
      };
      const r = await surgeryBudgetsAPI.preview(payload);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al calcular presupuesto');
    } finally {
      setLoadingPreview(false);
    }
  }, [procedureCode, orMinutes, orSpecial, stayCode, nights, selectedTestCodes, implantRows, notes, depositPercent, effectivePatientId]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(fetchPreview, 300);
    return () => clearTimeout(id);
  }, [open, fetchPreview]);

  const toggleTest = (code) => {
    setSelectedTestCodes((curr) =>
      curr.includes(code) ? curr.filter(c => c !== code) : [...curr, code]
    );
  };

  const toggleCategory = (cat) => {
    setOpenCategories((s) => ({ ...s, [cat]: !s[cat] }));
  };

  const handleSave = async () => {
    if (!procedureCode) {
      toast.error('Selecciona un procedimiento primero');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        patient_id: effectivePatientId,
        procedure_code: procedureCode,
        estimated_or_minutes: Number(orMinutes) || 0,
        or_special: !!orSpecial,
        stay_code: nights > 0 ? stayCode : null,
        nights: Number(nights) || 0,
        selected_test_codes: selectedTestCodes,
        selected_implants: implantRows
          .filter(r => r.reference && r.reference.trim())
          .map(r => ({
            reference: r.reference.trim(),
            brand: r.brand || null,
            volume_cc: r.volume_cc === '' ? null : Number(r.volume_cc),
            qty: Number(r.qty) || 1,
            unit_price: r.unit_price === '' || r.unit_price == null ? null : Number(r.unit_price),
            note: r.note || null,
          })),
        custom_lines: [],
        notes,
        deposit_percent: Number(depositPercent) || 50,
      };
      await surgeryBudgetsAPI.save(payload);
      toast.success('Presupuesto guardado');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar presupuesto');
    } finally {
      setSaving(false);
    }
  };

  const formatEUR = (n) =>
    n == null
      ? '—'
      : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="budget-generator-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-navy">
            <Calculator size={18} />
            Generar presupuesto de cirugía
            {effectivePatient && (
              <span className="text-sm font-normal text-gray-500">· {effectivePatient.name}</span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Selecciona procedimiento, duración de quirófano, estancia, pruebas opcionales y prótesis para calcular el presupuesto.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-2">
          {/* ───── Inputs column ───── */}
          <div className="lg:col-span-2 space-y-3">
            {/* Patient picker — only shown when the dialog was opened anonymously
                (i.e. no `patient` prop). When provided, the patient is shown in
                the dialog title and locked. The picker is optional: leaving it
                empty keeps the budget anonymous (legacy /admin/presupuestos flow). */}
            {!patient && (
              <Card className="p-2 space-y-1.5 border-indigo-100 bg-brand-soft/40/40">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold flex items-center gap-1 text-[#0E3463]">
                    <User size={12} /> Paciente
                    <span className="ml-1 font-normal text-gray-400">(opcional)</span>
                  </Label>
                  {!pickedPatient && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[11px] text-brand-navy hover:bg-brand-soft/40"
                      onClick={() => {
                        setCreatingPatient((v) => !v);
                        if (!creatingPatient) setPickedPatient(null);
                      }}
                      data-testid="budget-quick-create-patient-toggle"
                    >
                      {creatingPatient ? (
                        <><X size={11} className="mr-1" /> Cancelar</>
                      ) : (
                        <><Plus size={11} className="mr-1" /> Nuevo paciente rápido</>
                      )}
                    </Button>
                  )}
                </div>

                {creatingPatient ? (
                  <div className="space-y-1.5">
                    <div className="grid grid-cols-2 gap-1.5">
                      <Input
                        placeholder="Nombre completo *"
                        value={newPatientDraft.name}
                        onChange={(e) => setNewPatientDraft(d => ({ ...d, name: e.target.value }))}
                        className="h-8 text-[11px]"
                        data-testid="budget-quick-create-name"
                      />
                      <Input
                        placeholder="Teléfono *"
                        value={newPatientDraft.phone}
                        onChange={(e) => setNewPatientDraft(d => ({ ...d, phone: e.target.value }))}
                        className="h-8 text-[11px]"
                        data-testid="budget-quick-create-phone"
                      />
                      <Input
                        placeholder="DNI / Pasaporte"
                        value={newPatientDraft.dni}
                        onChange={(e) => setNewPatientDraft(d => ({ ...d, dni: e.target.value }))}
                        className="h-8 text-[11px]"
                        data-testid="budget-quick-create-dni"
                      />
                      <Input
                        placeholder="Email"
                        type="email"
                        value={newPatientDraft.email}
                        onChange={(e) => setNewPatientDraft(d => ({ ...d, email: e.target.value }))}
                        className="h-8 text-[11px]"
                        data-testid="budget-quick-create-email"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-3 text-[11px] bg-brand-navy hover:bg-brand-navy"
                        disabled={savingPatient || !newPatientDraft.name.trim() || !newPatientDraft.phone.trim()}
                        onClick={submitNewPatient}
                        data-testid="budget-quick-create-submit"
                      >
                        {savingPatient
                          ? <Loader2 size={11} className="mr-1 animate-spin" />
                          : <Save size={11} className="mr-1" />}
                        Crear y seleccionar
                      </Button>
                    </div>
                    <p className="text-[10px] text-gray-500">
                      Sólo nombre y teléfono son obligatorios. El paciente recién creado quedará
                      asignado automáticamente a este presupuesto.
                    </p>
                  </div>
                ) : (
                  <>
                    <PatientPicker
                      value={pickedPatient}
                      onChange={setPickedPatient}
                      placeholder="Buscar por nombre, DNI o NHC…"
                      testId="budget-patient-picker"
                    />
                    <p className="text-[10px] text-gray-500">
                      Si lo dejas en blanco, el presupuesto queda anónimo. Puedes asignar
                      un paciente más tarde desde la lista de presupuestos.
                    </p>
                  </>
                )}
              </Card>
            )}

            <div>
              <Label className="text-xs">Procedimiento</Label>
              <Select value={procedureCode || ''} onValueChange={setProcedureCode}>
                <SelectTrigger data-testid="budget-procedure-select">
                  <SelectValue placeholder="Selecciona un procedimiento…" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {procedures.map(p => (
                    <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProcedure && (
                <p className="text-[10px] text-gray-500 mt-1">
                  Cirujano: <strong>{formatEUR(selectedProcedure.surgeon_fee)}</strong> ·
                  Anestesista: <strong>{formatEUR(selectedProcedure.anesthesia_fee)}</strong>
                </p>
              )}
            </div>

            {/* Quirófano */}
            <Card className="p-2 space-y-2">
              <div className="text-xs font-semibold text-brand-navy">Quirófano</div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Cirugía especial (tarifa plana QMDE)</Label>
                <Switch
                  checked={orSpecial}
                  onCheckedChange={setOrSpecial}
                  data-testid="budget-or-special-switch"
                />
              </div>
              {!orSpecial && (
                <div>
                  <Label className="text-xs">Minutos estimados de quirófano</Label>
                  <Input
                    type="number"
                    step="5"
                    min="0"
                    value={orMinutes}
                    onChange={(e) => setOrMinutes(e.target.value)}
                    data-testid="budget-or-minutes"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Tramo elegido automáticamente (15 / 30 / 45 / 60 / 75 / 90 / 105 / 120 / 180 / 240 min + exceso/hora).
                  </p>
                </div>
              )}
            </Card>

            {/* Estancia */}
            <Card className="p-2 space-y-2">
              <div className="text-xs font-semibold text-brand-navy">Hospitalización</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tipo de estancia</Label>
                  <Select value={stayCode} onValueChange={setStayCode}>
                    <SelectTrigger data-testid="budget-stay-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SURGERY_STAY_CODES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Noches</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={nights}
                    onChange={(e) => setNights(e.target.value)}
                    data-testid="budget-nights"
                  />
                </div>
              </div>
            </Card>

            {/* Pruebas opcionales */}
            <Card className="p-2 space-y-1">
              <div className="text-xs font-semibold text-brand-navy mb-1">
                Pruebas opcionales{' '}
                {selectedTestCodes.length > 0 && (
                  <span className="text-[10px] font-normal text-gray-500">
                    ({selectedTestCodes.length} seleccionada/s)
                  </span>
                )}
              </div>
              {testCatalog.length === 0 ? (
                <p className="text-[10px] text-gray-400">Cargando catálogo de pruebas…</p>
              ) : (
                testCatalog.map(group => {
                  const isOpen = !!openCategories[group.category];
                  const groupCount = group.items.filter(i => selectedTestCodes.includes(i.test_code)).length;
                  return (
                    <div key={group.category} className="border-b last:border-b-0">
                      <button
                        type="button"
                        onClick={() => toggleCategory(group.category)}
                        className="w-full flex items-center justify-between py-1 px-1 text-left text-xs hover:bg-gray-50 rounded"
                        data-testid={`budget-test-category-${group.category}`}
                      >
                        <span className="flex items-center gap-1">
                          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          <span className="font-medium">
                            {TEST_CATEGORY_LABELS[group.category] || group.category_label}
                          </span>
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {groupCount}/{group.items.length}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="pl-3 pb-2 space-y-1">
                          {group.items.map(t => (
                            <label
                              key={t.test_code}
                              className="flex items-start gap-2 text-[11px] cursor-pointer hover:bg-gray-50 rounded p-1"
                            >
                              <Checkbox
                                checked={selectedTestCodes.includes(t.test_code)}
                                onCheckedChange={() => toggleTest(t.test_code)}
                                data-testid={`budget-test-${t.test_code}`}
                                className="mt-0.5"
                              />
                              <span className="flex-1">{t.label}</span>
                              <span className="font-mono text-gray-700">{formatEUR(t.price)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </Card>

            {selectedProcedure?.requires_implants && (
              <Card className="p-2 space-y-1 border-sky-200 bg-sky-50/30" data-testid="budget-implants-section">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-brand-navy">
                    Prótesis ({implantRows.length})
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() => setImplantRows(rs => [...rs, { reference: '', brand: '', volume_cc: '', qty: selectedProcedure?.implant_laterality === 'bilateral' ? 2 : 1, unit_price: '', note: '', resolved_label: '' }])}
                    data-testid="add-implant-row-btn"
                  >
                    <Plus size={12} className="mr-1" /> Añadir
                  </Button>
                </div>
                {implantRows.length === 0 ? (
                  <p className="text-[10px] text-gray-500 px-1">
                    Sin prótesis pre-seleccionadas. Configura los "Implantes por defecto" en
                    el procedimiento para que aparezcan aquí automáticamente, o pulsa "Añadir"
                    para introducir una referencia manualmente.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {/* Column headers */}
                    <div className="grid grid-cols-12 gap-1 px-1 text-[10px] uppercase tracking-wide text-gray-500 font-medium">
                      <div className="col-span-4">Referencia</div>
                      <div className="col-span-2 text-right">Vol. (cc)</div>
                      <div className="col-span-2 text-right">Uds.</div>
                      <div className="col-span-3 text-right">€/ud (IVA incl.)</div>
                      <div className="col-span-1"></div>
                    </div>
                    {implantRows.map((row, idx) => (
                      <div key={idx}>
                        <div className="grid grid-cols-12 gap-1 items-center text-[11px]">
                          <Input
                            placeholder="ej. SIL-30535-275XH"
                            value={row.reference}
                            onChange={(e) => setImplantRows(rs => rs.map((r, i) => i === idx ? { ...r, reference: e.target.value } : r))}
                            onBlur={async (e) => {
                              const ref = (e.target.value || '').trim();
                              if (!ref) return;
                              // Auto-resolve unit price if empty
                              if (row.unit_price === '' || row.unit_price == null) {
                                try {
                                  const r = await surgeryBudgetsAPI.resolveImplantReference(ref);
                                  if (r.data?.resolved && r.data.unit_price_with_vat != null) {
                                    setImplantRows(rs => rs.map((rr, i) => i === idx ? {
                                      ...rr,
                                      unit_price: r.data.unit_price_with_vat,
                                      resolved_label: r.data.label,
                                    } : rr));
                                  } else {
                                    setImplantRows(rs => rs.map((rr, i) => i === idx ? { ...rr, resolved_label: '' } : rr));
                                  }
                                } catch { /* ignore — preview will show ⚠ s/precio */ }
                              }
                            }}
                            className="col-span-4 h-8 text-[11px]"
                            data-testid={`implant-row-ref-${idx}`}
                          />
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="0"
                            value={row.volume_cc}
                            onChange={(e) => setImplantRows(rs => rs.map((r, i) => i === idx ? { ...r, volume_cc: e.target.value } : r))}
                            className="col-span-2 h-8 text-[11px] text-right"
                            data-testid={`implant-row-vol-${idx}`}
                          />
                          <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="1"
                            min="1"
                            value={row.qty}
                            onChange={(e) => setImplantRows(rs => rs.map((r, i) => i === idx ? { ...r, qty: e.target.value } : r))}
                            className="col-span-2 h-8 text-[11px] text-right"
                            data-testid={`implant-row-qty-${idx}`}
                          />
                          <Input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            placeholder="0,00"
                            value={row.unit_price}
                            onChange={(e) => setImplantRows(rs => rs.map((r, i) => i === idx ? { ...r, unit_price: e.target.value } : r))}
                            className="col-span-3 h-8 text-[11px] text-right"
                            data-testid={`implant-row-price-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="col-span-1 h-8 p-0"
                            onClick={() => setImplantRows(rs => rs.filter((_, i) => i !== idx))}
                            data-testid={`implant-row-remove-${idx}`}
                          >
                            <X size={14} className="text-red-500" />
                          </Button>
                        </div>
                        {row.resolved_label && (
                          <div className="text-[10px] text-emerald-600 ml-1 mt-0.5">
                            ✓ {row.resolved_label}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            <div>
              <Label className="text-xs">Notas adicionales (al pie del PDF)</Label>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej. promoción aplicada, descuento profesional, etc."
                data-testid="budget-notes"
              />
            </div>

            <div>
              <Label className="text-xs">% de adelanto (para reservar la fecha)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min={10}
                  max={100}
                  step={5}
                  value={depositPercent}
                  onChange={(e) => setDepositPercent(e.target.value)}
                  className="w-24"
                  data-testid="budget-deposit-percent"
                />
                <span className="text-sm text-gray-600">% de los honorarios del cirujano</span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Por defecto 50%. Baja a 30-40% para reservar casos pequeños o exige más en cirugías complejas.</p>
            </div>
          </div>

          {/* ───── Preview column ───── */}
          <div className="lg:col-span-3">
            <Card className="p-3 sticky top-0" data-testid="budget-preview">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-brand-navy">Borrador</h3>
                {loadingPreview && <Loader2 size={14} className="animate-spin text-gray-400" />}
              </div>
              {preview ? (
                <>
                  {preview.incomplete?.length > 0 && (
                    <div className="mb-2 p-2 bg-brand-soft/40 border border-brand-medium rounded text-xs flex items-start gap-2 text-brand-deep">
                      <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>{preview.incomplete.length} línea/s sin precio configurado.</strong>{' '}
                        Configura precios en <code>/admin/presupuestos</code> → Configuración.
                      </span>
                    </div>
                  )}
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b">
                        <th className="text-left py-1">Concepto</th>
                        <th className="text-right py-1 w-14">Uds.</th>
                        <th className="text-right py-1 w-24">Precio</th>
                        <th className="text-right py-1 w-24">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.lines.map((l, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-1">{l.label}</td>
                          <td className="text-right">{l.quantity}</td>
                          <td className="text-right">
                            {l.unit_price == null ? (
                              <span className="text-brand-deep">⚠ s/precio</span>
                            ) : formatEUR(l.unit_price)}
                          </td>
                          <td className="text-right">{formatEUR(l.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-semibold">
                        <td colSpan={3} className="text-right py-2">Total</td>
                        <td className="text-right py-2 text-brand-navy">{formatEUR(preview.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              ) : (
                <p className="text-xs text-gray-400">Selecciona parámetros para ver el borrador.</p>
              )}
            </Card>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="budget-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !preview || !procedureCode}
            data-testid="budget-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Guardar borrador
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
